import PropTypes from 'prop-types';
import React from 'react';
import { createPortal } from 'react-dom';

import { getClients } from 'common-src/models/Client';

import useCustomSelector from 'src/hooks/useCustomSelector';

import { ESCALATED_TO_OPTIONS } from '../../constants';
import {
  buildEmptyEscalationTeamGroups,
  buildEscalationPayload,
  groupClientsByEscalationTeam,
  isEscalationPayloadValid,
} from '../../helpers';
import styles from './EscalateTaskModal.module.scss';

const EscalateTaskModal = ({ onClose, onConfirm, isSubmitting = false, currentUserName = '' }) => {
  const clients = useCustomSelector((state) => getClients(state)) || [];

  const [selectedTeam, setSelectedTeam] = React.useState(null);
  const [selectedClientId, setSelectedClientId] = React.useState(null);
  const [escalatedReason, setEscalatedReason] = React.useState('');
  const [isSpecificPersonChecked, setIsSpecificPersonChecked] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [openTeams, setOpenTeams] = React.useState({});

  const clientsByTeam = React.useMemo(() => groupClientsByEscalationTeam(clients), [clients]);

  const filteredClientsByTeam = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return clientsByTeam;

    const grouped = buildEmptyEscalationTeamGroups();

    Object.entries(clientsByTeam).forEach(([teamValue, teamClients]) => {
      grouped[teamValue] = teamClients.filter((item) => {
        const haystack = `${item.label} ${item.role}`.toLowerCase();
        return haystack.includes(normalizedQuery);
      });
    });

    return grouped;
  }, [clientsByTeam, query]);
  const isSearching = query.trim().length > 0;
  const flatFilteredClients = React.useMemo(() => {
    const seenClientIds = new Set();
    const matches = [];

    ESCALATED_TO_OPTIONS.forEach((option) => {
      (filteredClientsByTeam[option.value] || []).forEach((item) => {
        if (seenClientIds.has(item.id)) return;

        seenClientIds.add(item.id);
        matches.push({
          ...item,
          teamValue: option.value,
          teamLabel: option.label,
        });
      });
    });

    return matches;
  }, [filteredClientsByTeam]);

  React.useEffect(() => {
    if (!isSpecificPersonChecked) return;

    const nextOpenTeams = {};
    ESCALATED_TO_OPTIONS.forEach((option, index) => {
      nextOpenTeams[option.value] = index === 0;
    });
    setOpenTeams(nextOpenTeams);
  }, [isSpecificPersonChecked]);

  const toggleTeam = (teamValue) => {
    setOpenTeams((prev) => ({
      ...prev,
      [teamValue]: !prev[teamValue],
    }));
  };

  const handleSpecificPersonChange = (event) => {
    const { checked } = event.target;
    setIsSpecificPersonChecked(checked);

    if (checked) {
      setSelectedTeam(null);
      return;
    }

    setSelectedClientId(null);
    setQuery('');
  };

  const handleSelectClient = (teamValue, clientId) => {
    setSelectedTeam(teamValue);
    setSelectedClientId(clientId);
  };

  const handleSelectTeam = (teamValue) => {
    setSelectedTeam(teamValue);
    setSelectedClientId(null);
  };

  const escalationPayload = buildEscalationPayload({
    escalatedReason,
    selectedTeam,
    selectedClientId,
    isSpecificPersonChecked,
  });

  const isEscalateDisabled = isSubmitting || !isEscalationPayloadValid(escalationPayload);
  const selectedTeamLabel =
    ESCALATED_TO_OPTIONS.find((option) => option.value === selectedTeam)?.label ?? null;
  const selectedClientLabel =
    flatFilteredClients.find((item) => item.id === selectedClientId)?.label ??
    clientsByTeam[selectedTeam]?.find((item) => item.id === selectedClientId)?.label ??
    null;
  const primaryActionLabel = isSpecificPersonChecked
    ? selectedClientLabel
      ? `Escalate to ${selectedClientLabel}`
      : 'Escalate'
    : selectedTeamLabel
      ? `Escalate to ${selectedTeamLabel}`
      : 'Escalate';

  const modalContent = (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="escalate-task-title"
      >
        <div className={styles.modalHeader}>
          <div id="escalate-task-title" className={styles.modalTitle}>
            Escalate task
          </div>
          <div className={styles.modalSubtitle}>
            Flags this task to another user or team. You remain the owner.
          </div>
        </div>

        <div className={styles.modalBody}>
          {!isSpecificPersonChecked ? (
            <>
              <div className={styles.sectionLabel}>Escalate to team</div>

              <div className={styles.teamGrid}>
                {ESCALATED_TO_OPTIONS.map((option) => {
                  const isActive = selectedTeam === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`${styles.teamCard} ${isActive ? styles.teamCardActive : ''}`}
                      onClick={() => handleSelectTeam(option.value)}
                    >
                      <span className={styles.teamIcon} aria-hidden="true">
                        {option.icon}
                      </span>
                      <span className={styles.teamLabel}>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className={styles.userPicker}>
              <div className={styles.searchWrap}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search people..."
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>

              <div className={styles.teamSections}>
                {isSearching ? (
                  !flatFilteredClients.length ? (
                    <div className={styles.teamSectionEmpty}>No people found.</div>
                  ) : (
                    <div className={styles.teamSectionList}>
                      {flatFilteredClients.map((item) => {
                        const isSelected = selectedClientId === item.id;
                        const isYou =
                          !!currentUserName &&
                          item.label.toLowerCase() === currentUserName.trim().toLowerCase();

                        return (
                          <button
                            key={`${item.teamValue}-${item.id}`}
                            type="button"
                            className={`${styles.teamMemberRow} ${
                              isSelected ? styles.teamMemberRowActive : ''
                            }`}
                            onClick={() => handleSelectClient(item.teamValue, item.id)}
                          >
                            <span className={styles.teamMemberAvatar}>{item.initials}</span>

                            <span className={styles.teamMemberInfo}>
                              <span className={styles.teamMemberNameRow}>
                                <span className={styles.teamMemberName}>{item.label}</span>
                                {isYou ? <span className={styles.teamMemberYou}>(you)</span> : null}
                              </span>
                              <span className={styles.teamMemberRole}>{item.role || '—'}</span>
                            </span>

                            <span className={styles.teamMemberBadge}>{item.teamLabel}</span>
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : (
                  ESCALATED_TO_OPTIONS.map((option) => {
                    const teamClients = filteredClientsByTeam[option.value] || [];
                    const isOpen = !!openTeams[option.value];

                    return (
                      <div key={option.value} className={styles.teamSection}>
                        <button
                          type="button"
                          className={styles.teamSectionHeader}
                          onClick={() => toggleTeam(option.value)}
                        >
                          <span className={styles.teamSectionLeft}>
                            <span
                              className={`${styles.teamSectionChevron} ${
                                isOpen ? styles.teamSectionChevronOpen : ''
                              }`}
                              aria-hidden="true"
                            >
                              ›
                            </span>
                            <span className={styles.teamSectionIcon} aria-hidden="true">
                              {option.icon}
                            </span>
                            <span className={styles.teamSectionTitle}>{option.label}</span>
                          </span>
                          <span className={styles.teamSectionCount}>{teamClients.length}</span>
                        </button>

                        {isOpen ? (
                          <div className={styles.teamSectionList}>
                            {!teamClients.length ? (
                              <div className={styles.teamSectionEmpty}>No people found.</div>
                            ) : (
                              teamClients.map((item) => {
                                const isSelected = selectedClientId === item.id;
                                const isYou =
                                  !!currentUserName &&
                                  item.label.toLowerCase() === currentUserName.trim().toLowerCase();

                                return (
                                  <button
                                    key={item.id}
                                    type="button"
                                    className={`${styles.teamMemberRow} ${
                                      isSelected ? styles.teamMemberRowActive : ''
                                    }`}
                                    onClick={() => handleSelectClient(option.value, item.id)}
                                  >
                                    <span className={styles.teamMemberAvatar}>{item.initials}</span>

                                    <span className={styles.teamMemberInfo}>
                                      <span className={styles.teamMemberNameRow}>
                                        <span className={styles.teamMemberName}>{item.label}</span>
                                        {isYou ? (
                                          <span className={styles.teamMemberYou}>(you)</span>
                                        ) : null}
                                      </span>
                                      <span className={styles.teamMemberRole}>
                                        {item.role || '—'}
                                      </span>
                                    </span>

                                    <span className={styles.teamMemberBadge}>{option.label}</span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <div className={styles.checkboxSection}>
            <label htmlFor="escalate-specific-person" className={styles.checkboxRow}>
              <input
                id="escalate-specific-person"
                type="checkbox"
                checked={isSpecificPersonChecked}
                onChange={handleSpecificPersonChange}
              />
              <span className={styles.checkboxLabel}>Escalate to a specific person</span>
            </label>
          </div>

          <textarea
            className={styles.reasonInput}
            placeholder="Add context (optional)"
            value={escalatedReason}
            onChange={(event) => setEscalatedReason(event.target.value)}
            rows={4}
          />
        </div>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.modalSecondaryAction}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.modalPrimaryAction}
            disabled={isEscalateDisabled}
            onClick={async () => {
              const shouldClose = await onConfirm?.(escalationPayload);

              if (shouldClose !== false) onClose();
            }}
          >
            {isSubmitting ? 'Escalating...' : primaryActionLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;

  return createPortal(modalContent, document.body);
};

EscalateTaskModal.propTypes = {
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  isSubmitting: PropTypes.bool,
  currentUserName: PropTypes.string,
};

export default EscalateTaskModal;
